/**
 * Accessible image slider/carousel.
 * Supports keyboard navigation, touch swipe, and reduced motion.
 */
function initImageSliders() {
  const sliders = document.querySelectorAll('[data-image-slider]');

  sliders.forEach((viewport) => {
    const slides = Array.from(viewport.querySelectorAll('.image-slider__slide'));
    const prevBtn = viewport.querySelector('[data-slider-prev]');
    const nextBtn = viewport.querySelector('[data-slider-next]');
    const dots = Array.from(viewport.querySelectorAll('[data-slider-dot]'));
    const pagination = viewport.querySelector('[data-max-dots]');
    const maxVisibleDots = parseInt(pagination?.dataset.maxDots, 10) || 7;

    if (slides.length <= 1) {
      return;
    }

    let currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (currentIndex < 0) {
      currentIndex = 0;
    }

    let touchStartX = 0;
    let touchEndX = 0;
    const swipeThreshold = 50;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function goToSlide(index) {
      const total = slides.length;
      const nextIndex = ((index % total) + total) % total;

      slides.forEach((slide, i) => {
        const isActive = i === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
      });

      updateDots(nextIndex);

      currentIndex = nextIndex;
    }

    function updateDots(activeIndex) {
      const total = dots.length;
      const windowSize = Math.min(maxVisibleDots, total);
      let start = 0;

      if (total > windowSize) {
        const half = Math.floor(windowSize / 2);
        start = activeIndex - half;
        start = Math.max(0, Math.min(start, total - windowSize));
      }

      const end = start + windowSize - 1;
      const hasSlidesBefore = start > 0;
      const hasSlidesAfter = end < total - 1;

      dots.forEach((dot, index) => {
        const isVisible = index >= start && index <= end;
        const isActive = index === activeIndex;
        const showsMoreSlides = isVisible && !isActive && (
          (hasSlidesBefore && index === start) || (hasSlidesAfter && index === end)
        );

        dot.hidden = !isVisible;
        dot.classList.toggle('is-active', isActive);
        dot.classList.toggle('is-more', showsMoreSlides);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    function goNext() {
      goToSlide(currentIndex + 1);
    }

    function goPrev() {
      goToSlide(currentIndex - 1);
    }

    prevBtn?.addEventListener('click', goPrev);
    nextBtn?.addEventListener('click', goNext);

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.slideIndex, 10);
        goToSlide(index);
      });
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'Home') {
        event.preventDefault();
        goToSlide(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        goToSlide(slides.length - 1);
      }
    });

    viewport.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0].screenX;
    }, { passive: true });

    viewport.addEventListener('touchend', (event) => {
      touchEndX = event.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) < swipeThreshold) {
        return;
      }

      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }, { passive: true });

    updateDots(currentIndex);

    if (!prefersReducedMotion) {
      viewport.classList.add('js-animated');
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageSliders);
} else {
  initImageSliders();
}

export default initImageSliders;
